/**
 * /verify-email — 6-digit OTP code from Clerk's `email_code` flow.
 *
 * Flow:
 *   - Reads the in-progress `SignUpResource` from `useSignUp()`.
 *   - When all 6 digits are entered, auto-submits
 *     `signUp.attemptEmailAddressVerification({ code })`.
 *   - On `complete`, `setActive({ session: createdSessionId })` and
 *     navigate to `/onboarding`.
 *   - "Resend code" calls `prepareEmailAddressVerification` again,
 *     gated by a 30s cooldown (UX, not enforced server-side).
 */

import { Button } from '@/components/ui/button';
import { OTPField, OTPFieldInput } from '@/components/ui/otp-field';
import { activateSession, attemptEmailVerification, prepareEmailVerification } from '@/lib/auth';
import { useSignUp } from '@clerk/react/legacy';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { type ReactElement, useEffect, useRef, useState } from 'react';

const RESEND_COOLDOWN_SECONDS = 30;

export const Route = createFileRoute('/_auth/verify-email')({
  component: VerifyEmailPage,
});

function VerifyEmailPage(): ReactElement {
  const { signUp, setActive, isLoaded } = useSignUp();
  const navigate = useNavigate();

  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | undefined>(undefined);
  const [codeError, setCodeError] = useState<string | undefined>(undefined);
  const [cooldown, setCooldown] = useState(RESEND_COOLDOWN_SECONDS);
  const [resendNotice, setResendNotice] = useState<string | undefined>(undefined);

  // Ref ensures we don't double-submit when the OTP auto-completes
  // twice due to React 19 strict-mode re-rendering.
  const submittingRef = useRef(false);

  useEffect(() => {
    if (cooldown <= 0) return;
    const id = window.setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => window.clearTimeout(id);
  }, [cooldown]);

  async function attempt(value: string) {
    if (!isLoaded || !signUp || !setActive) return;
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    setFormError(undefined);
    setCodeError(undefined);

    const result = await attemptEmailVerification(signUp, value);

    if (result.kind === 'error') {
      setCodeError(result.fieldErrors.code);
      setFormError(result.fieldErrors.code ? undefined : result.formError);
      setCode('');
      submittingRef.current = false;
      setSubmitting(false);
      return;
    }

    if (result.kind === 'complete') {
      await activateSession({ setActive, sessionId: result.sessionId, redirectUrl: '/onboarding' });
      return;
    }

    // missing_requirements — rare for email+password, but surface it.
    setFormError('Almost there — a few more steps are required.');
    navigate({ to: '/onboarding' });
  }

  function onChange(value: string) {
    setCode(value);
    if (value.length === 6) {
      void attempt(value);
    }
  }

  async function onResend() {
    if (!isLoaded || !signUp) return;
    setResendNotice(undefined);
    setFormError(undefined);
    const result = await prepareEmailVerification(signUp);
    if (result.kind === 'error') {
      setFormError(result.formError ?? 'Unable to resend code. Try again.');
      return;
    }
    setCooldown(RESEND_COOLDOWN_SECONDS);
    setResendNotice('New code sent.');
  }

  if (!isLoaded) {
    return (
      <div className="flex items-center justify-center p-6 text-sm text-[var(--color-mute)]">
        Loading…
      </div>
    );
  }

  if (!signUp || signUp.status === null) {
    return (
      <div className="space-y-3 text-center">
        <h1 className="text-xl font-semibold text-[var(--color-ink)]">No pending sign-up</h1>
        <p className="text-sm text-[var(--color-mute)]">Start again from the sign-up page.</p>
        <Button onClick={() => navigate({ to: '/sign-up' })}>Sign up</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <header className="space-y-1 text-center">
        <h1 className="text-xl font-semibold text-[var(--color-ink)]">Check your email</h1>
        <p className="text-sm text-[var(--color-mute)]">
          Enter the 6-digit code we sent to{' '}
          <span className="font-medium text-[var(--color-ink)]">
            {signUp.emailAddress ?? 'your email address'}
          </span>
          .
        </p>
      </header>
      <div className="flex flex-col items-center gap-3">
        <OTPField length={6} value={code} onValueChange={onChange} disabled={submitting}>
          {Array.from({ length: 6 }).map((_, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: fixed-length OTP slots
            <OTPFieldInput key={i} />
          ))}
        </OTPField>
        {codeError && (
          <p className="text-xs text-[var(--color-danger)]" role="alert">
            {codeError}
          </p>
        )}
      </div>
      {formError && (
        <div
          className="rounded-md border border-[var(--color-danger)]/30 bg-[var(--color-danger)]/10 px-3 py-2 text-sm text-[var(--color-danger)]"
          role="alert"
        >
          {formError}
        </div>
      )}
      <div className="flex flex-col items-center gap-2 text-sm">
        <button
          type="button"
          onClick={onResend}
          disabled={cooldown > 0}
          className="font-medium text-[var(--color-accent-brand)] hover:underline disabled:pointer-events-none disabled:text-[var(--color-mute)]"
        >
          {cooldown > 0 ? `Resend code in ${cooldown}s` : 'Resend code'}
        </button>
        {resendNotice && <p className="text-xs text-[var(--color-mute)]">{resendNotice}</p>}
      </div>
    </div>
  );
}
